import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Slider,
  Button,
  Card,
  LinearProgress,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
} from "@mui/material";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Edit, Replay } from "@mui/icons-material";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import {
  setTrustAttributes,
  setRecommendations,
  setTrustIndexData,
  setLoading as setTrustIndexLoading,
  setError as setTrustIndexError,
} from "../store/slices/trustIndexSlice";
import {
  setApplicationSetupData,
  setLoading as setAppSetupLoading,
} from "../store/slices/applicationSetupSlice";
import LoadingOverlay from "../components/LoadingOverlay";
import ProjectRequired from "../components/ProjectRequired";
import { ReactComponent as InputsSVG } from "../asserts/logo/Inputs.svg";

const ATTRIBUTES = [
  { label: "Accuracy", color: "#1CE017" },
  { label: "Fairness", color: "#9ED2FF" },
  { label: "Security", color: "#EC3AD1" },
  { label: "Privacy", color: "#EEC213" },
  { label: "Robustness", color: "#FF7B2E" },
  { label: "Explainability", color: "#6FFFB0" },
  { label: "Ethics", color: "#7823BA" },
];

const baseUrl = process.env.REACT_APP_API_URL || "https://validaite.io/api";
const SIDEBAR_WIDTH = 0;

const sliderStyles = (color) => ({
  height: 8,
  borderRadius: 4,

  "& .MuiSlider-track": {
    height: 8,
    borderRadius: 4,
    backgroundColor: color,
    border: "none",
  },

  "& .MuiSlider-rail": {
    opacity: 0.15,
    height: 8,
    borderRadius: 4,
    backgroundColor: "#E5E7EB",
  },

  "& .MuiSlider-thumb": {
    height: 18,
    width: 18,
    backgroundColor: "#FFFFFF",
    border: "none",
    boxShadow: "0px 1px 3px rgba(0,0,0,0.18)",
    "&:hover": {
      boxShadow: "0px 1px 4px rgba(0,0,0,0.25)",
    },
  },

  // 🔥 remove focus outline blue border
  "& .MuiSlider-thumb:focus, & .MuiSlider-thumb:active, & .MuiSlider-thumb.Mui-focusVisible":
    {
      boxShadow: "0px 1px 3px rgba(0,0,0,0.18)",
      outline: "none",
      border: "none",
    },

  // 🔥 remove track focus glow
  "&:focus, &:active, &.Mui-focusVisible": {
    outline: "none",
    border: "none",
    boxShadow: "none",
  },
});

const TrustIndexSettingsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  // const { projectId } = useParams();

  // Get data from location.state if coming from navigation
  const {
    applicationName: appNameFromLocation,
    domain: domainFromLocation,
    archetype: archetypeFromLocation,
    riskLevel: riskLevelFromLocation,
    description: descriptionFromLocation,
    modifiers: modifiersFromLocation = [],
    archetypeDescription,
    confidence,
    focusAreas,
    riskCode,
    useCases,
    recommendations,
  } = location.state || {};

  // Redux state
  const weights = useSelector((state) => state.trustIndex.trustAttributes);
  const recommendedWeights = useSelector(
    (state) => state.trustIndex.recommendations
  );
  const loading = useSelector((state) => state.trustIndex.loading);
  const error = useSelector((state) => state.trustIndex.error);
  const projectId = useSelector((state) => state.project.currentProjectId);

  // Get application setup data from Redux
  const appSetupBasicInfo = useSelector(
    (state) => state.applicationSetup.basicInfo
  );
  const appSetupClassification = useSelector(
    (state) => state.applicationSetup.classificationResult
  );

  // Use Redux data if available, otherwise use location.state
  const applicationName =
    appNameFromLocation || appSetupBasicInfo.applicationName;
  const domain = domainFromLocation || appSetupBasicInfo.domain;
  const description = descriptionFromLocation || appSetupBasicInfo.description;
  const modifiers =
    modifiersFromLocation.length > 0
      ? modifiersFromLocation
      : appSetupBasicInfo.modifiers;
  const archetype =
    archetypeFromLocation || appSetupClassification?.classification?.archetype;
  const riskLevel =
    riskLevelFromLocation || appSetupClassification?.riskProfile?.level;

  // Local UI state (not persisted)
  const [isEditable, setIsEditable] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [targetTrustIndex, setTargetTrustIndex] = useState(89); // Default 89%

  const totalWeight = Object.values(weights).reduce((sum, v) => sum + v, 0);

  // Fetch application setup data from database if not in Redux
  useEffect(() => {
    const fetchApplicationSetupData = async () => {
      if (!projectId) {
        console.log("No projectId, skipping application setup fetch");
        return;
      }

      // Only fetch if we don't have the data in Redux
      if (!appSetupBasicInfo.applicationName) {
        try {
          dispatch(setAppSetupLoading(true));
          const response = await axios.get(
            `${baseUrl}/application-setup?project_id=${projectId}`
          );

          if (response.data.success && response.data.data) {
            console.log("Fetched application setup data:", response.data.data);
            dispatch(setApplicationSetupData(response.data.data));
          }
        } catch (err) {
          console.error("Error fetching application setup:", err);
        } finally {
          dispatch(setAppSetupLoading(false));
        }
      }
    };

    fetchApplicationSetupData();
  }, [projectId, appSetupBasicInfo.applicationName, dispatch]);

  // Fetch trust index data from database on mount
  useEffect(() => {
    const fetchTrustIndexData = async () => {
      if (!projectId) {
        console.log("No projectId, skipping fetch");
        return;
      }

      try {
        dispatch(setTrustIndexLoading(true));
        const response = await axios.get(
          `${baseUrl}/trust-index-settings?project_id=${projectId}`
        );

        if (response.data.success && response.data.data) {
          console.log("Fetched trust index data:", response.data.data);

          // Map backend data structure to frontend Redux structure
          const { recommendedWeights, currentWeights, rationale, targetTrustIndex: savedTargetIndex } =
            response.data.data;

          // Check if currentWeights has actual non-zero values
          const hasCurrentWeights = currentWeights &&
            Object.keys(currentWeights).length > 0 &&
            Object.values(currentWeights).some(v => v > 0);

          // Check if recommendedWeights has actual non-zero values
          const hasRecommendedWeights = recommendedWeights &&
            Object.keys(recommendedWeights).length > 0 &&
            Object.values(recommendedWeights).some(v => v > 0);

          // Set trust attributes: prefer currentWeights, fallback to recommendedWeights
          if (hasCurrentWeights) {
            dispatch(setTrustAttributes(currentWeights));
          } else if (hasRecommendedWeights) {
            // Use recommended weights if no current weights are set
            dispatch(setTrustAttributes(recommendedWeights));
          }

          if (hasRecommendedWeights) {
            dispatch(setRecommendations(recommendedWeights));
          }

          // Load saved targetTrustIndex if available
          if (savedTargetIndex !== undefined && savedTargetIndex !== null) {
            setTargetTrustIndex(savedTargetIndex);
          }
        }
      } catch (err) {
        console.error("Error fetching trust index:", err);
        dispatch(setTrustIndexError("Failed to load data"));
      } finally {
        dispatch(setTrustIndexLoading(false));
      }
    };

    fetchTrustIndexData();
  }, [projectId, dispatch]);

  /** === Handle Backend Call === */
  const handleGenerateWeight = async () => {
    dispatch(setTrustIndexLoading(true));
    dispatch(setTrustIndexError(null));
    setSuccessMessage(null);

    if (!projectId) {
      dispatch(
        setTrustIndexError(
          "No project selected. Please select a project first."
        )
      );
      dispatch(setTrustIndexLoading(false));
      return;
    }

    try {
      const response = await axios.post(`${baseUrl}/trust-index-settings`, {
        project_id: projectId,
        applicationSummary: {
          applicationName,
          archetypeCode: archetype,
          riskTier: riskLevel,
          domain,
          description,
          modifiers,
        },
        currentWeights: weights,
      });

      if (response.data.success && response.data.data?.recommendedWeights) {
        dispatch(setRecommendations(response.data.data.recommendedWeights));
        dispatch(setTrustAttributes(response.data.data.recommendedWeights));
        // Set LLM-generated targetTrustIndex if available
        if (response.data.data.targetTrustIndex) {
          setTargetTrustIndex(response.data.data.targetTrustIndex);
        }
        setSuccessMessage(" Weights and Trust Index Score generated successfully via LLM!");
      } else {
        dispatch(setTrustIndexError("Failed to generate weights from LLM."));
      }
    } catch (err) {
      console.error(err);
      dispatch(
        setTrustIndexError("Failed to generate weights. Check backend logs.")
      );
    } finally {
      dispatch(setTrustIndexLoading(false));
    }
  };

  /** === Equal Split === */
  // const handleEqualSplit = () => {
  //   const equalValue = Math.floor(100 / ATTRIBUTES.length);
  //   const remainder = 100 - equalValue * ATTRIBUTES.length;
  //   const newWeights = ATTRIBUTES.reduce((acc, attr, i) => {
  //     acc[attr.label] = i === 0 ? equalValue + remainder : equalValue;
  //     return acc;
  //   }, {});
  //   setWeights(newWeights);
  //   setError(null);
  //   setSuccessMessage(" Weights split equally across all attributes.");
  // };

  /** === Slider Change === */
  const handleManualChange = (attr, value) => {
    const newWeights = { ...weights, [attr]: value };
    dispatch(setTrustAttributes(newWeights));
    setSuccessMessage(null);
    if (Object.values(newWeights).reduce((a, b) => a + b, 0) > 100) {
      dispatch(
        setTrustIndexError(
          "❌ Weight has exceeded the given limit. Please reset the weights."
        )
      );
    } else {
      dispatch(setTrustIndexError(null));
    }
  };

  /** === Edit / Reset === */
  const handleEditOrReset = () => {
    if (isEditable) {
      setIsEditable(false);
    } else {
      setIsEditable(true);
    }
  };

  const handleReset = () => {
    const emptyWeights = ATTRIBUTES.reduce(
      (acc, attr) => ({ ...acc, [attr.label]: 0 }),
      {}
    );
    dispatch(setTrustAttributes(emptyWeights));
    dispatch(setTrustIndexError(null));
  };

  /** === Next === */
  const handleNext = async () => {
    if (!projectId) {
      dispatch(
        setTrustIndexError(
          "No project selected. Please select a project first."
        )
      );
      return;
    }

    try {
      dispatch(setTrustIndexLoading(true));

      // Save trust index data to database before navigating (saveOnly mode - no LLM call)
      const response = await axios.post(`${baseUrl}/trust-index-settings`, {
        project_id: projectId,
        applicationSummary: {
          applicationName,
          archetypeCode: archetype,
          riskTier: riskLevel,
          domain,
          description,
          modifiers,
        },
        currentWeights: weights,
        targetTrustIndex: targetTrustIndex, // Include target trust index
        saveOnly: true,
      });

      if (response.data.success) {
        console.log("Trust index data saved before navigation");

        // Navigate to next page
        navigate(`/project/${projectId}/risk-identification`, {
          state: {
            applicationName,
            domain,
            archetype,
            archetypeDescription,
            confidence,
            focusAreas,
            riskCode,
            riskLevel,
            description,
            modifiers,
            trustWeights: weights,
            useCases,
            recommendations,
          },
        });
      } else {
        dispatch(setTrustIndexError("Failed to save trust index data"));
      }
    } catch (err) {
      console.error("Error saving trust index data:", err);
      dispatch(setTrustIndexError("Failed to save data. Please try again."));
    } finally {
      dispatch(setTrustIndexLoading(false));
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        bgcolor: "#f3f2f5ff",
        minHeight: "100vh",
        ml: { xs: 0, md: `${SIDEBAR_WIDTH}px` },
        fontFamily: "'Inter', sans-serif",
      }}
    >
      {/* === Sticky Header ===
      <Box
        sx={{
          position: "sticky",
          top: 50,
          zIndex: 100,
          backgroundColor: "#f3f2f5ff",
          py: 2.5,
          px: 2,
        }}
      >
        <Typography variant="h5" fontWeight={600} color="#111827">
          Trust Index Settings
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Actual text should be added here
        </Typography>
      </Box> */}

      {/* === Sticky Header === */}
      <Box
        sx={{
          position: "sticky",
          top: 50,
          zIndex: 120,
          backgroundColor: "#f6f3f2",
          pb: 1,
        }}
      >
        {/* Header Title */}
        <Box sx={{ py: 2.5, px: 3 }}>
          <Typography variant="h5" fontWeight={600} color="#111827">
            Set Trust Attributes Weights
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Trustworthiness Dimension Prioritization Framework...
          </Typography>
        </Box>

        {/* 🚨 Error Message Bar — Fixed to Top Below Header */}
        {error && (
          <Box
            sx={{
              backgroundColor: "#FEE2E2",
              border: "1px solid #FCA5A5",
              borderRadius: "6px",
              mx: 3,
              mb: 1,
              px: 2,
              py: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              animation: "shake 0.2s ease-in-out",
              "@keyframes shake": {
                "0%, 100%": { transform: "translateX(0)" },
                "25%": { transform: "translateX(-3px)" },
                "75%": { transform: "translateX(3px)" },
              },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "#B91C1C",
                fontWeight: 600,
                flex: 1,
              }}
            >
              ⚠️ {error}
            </Typography>
            <Button
              size="small"
              // onClick={() => setError(null)}
              sx={{
                textTransform: "none",
                color: "#B91C1C",
                fontWeight: 600,
                minWidth: "auto",
              }}
            >
              ✕
            </Button>
          </Box>
        )}
      </Box>

      {/* === Main Content === */}
      <Box sx={{ p: 4, pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3, fontWeight: 600 }}>
            {error}
          </Alert>
        )}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {successMessage}
          </Alert>
        )}

        {/* === Card 1: Inputs from Previous Stage === */}
        <Card
          sx={{
            mb: 3,
            p: 3,
            borderRadius: 0,
            boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
            bgcolor: "#fff",
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight={700}
            mb={2}
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <InputsSVG style={{ width: 22, height: 22 }} /> Inputs from Previous
            Stage
          </Typography>

          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="#6b7280">
                Application
              </Typography>
              <Typography variant="subtitle2" fontWeight={500}>
                {applicationName || "Not specified"}
              </Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="#6b7280">
                Archetype Code
              </Typography>
              <Typography variant="subtitle2" fontWeight={500}>
                {archetype || "Not specified"}
              </Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="#6b7280">
                Risk Tier
              </Typography>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color={
                  riskLevel?.toLowerCase() === "critical"
                    ? "#DC2626"
                    : riskLevel?.toLowerCase() === "high"
                    ? "#DC2626"
                    : riskLevel?.toLowerCase() === "medium"
                    ? "#F97316"
                    : riskLevel?.toLowerCase() === "low"
                    ? "#16A34A"
                    : "#16A34A"
                }
              >
                {riskLevel || "Not specified"}
              </Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="#6b7280">
                Domain
              </Typography>
              <Typography variant="subtitle2" fontWeight={500}>
                {domain || "Not specified"}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  color="#111827"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  Modifiers:
                </Typography>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {modifiers.length > 0 ? (
                    modifiers.map((mod) => (
                      <Chip
                        key={mod}
                        label={mod}
                        sx={{
                          bgcolor:
                            mod === "PII"
                              ? "#E8F1FF"
                              : mod === "RAG"
                              ? "#E1F5FF"
                              : mod === "ENT"
                              ? "#E1F5FF"
                              : "#F3F4F6",
                          color:
                            mod === "PII"
                              ? "#0056B3"
                              : mod === "RAG"
                              ? "#0094FB"
                              : mod === "ENT"
                              ? "#0094FB"
                              : "#374151",
                          fontWeight: 600,
                        }}
                      />
                    ))
                  ) : (
                    <Typography variant="subtitle2" fontWeight={500}>
                      Not specified
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Card>

        {/* === Action Buttons === */}
        <Box display="flex" gap={2} mb={3}>
          <Button
            variant="contained"
            onClick={handleGenerateWeight}
            startIcon={loading ? <CircularProgress size={16} /> : null}
            disabled={loading}
            sx={{
              bgcolor: "#3B82F6",
              textTransform: "none",
              borderRadius: 2,
              px: 3,
              "&:hover": { bgcolor: "#2563EB" },
            }}
          >
            {loading ? "Generating..." : "Generate Weights"}
          </Button>
          {/* <Button
            variant="outlined"
            onClick={handleEqualSplit}
            sx={{
              borderColor: "#93C5FD",
              color: "#2563EB",
              textTransform: "none",
              borderRadius: 2,
              px: 3,
              "&:hover": { borderColor: "#2563EB", background: "#EFF6FF" },
            }}
          >
            Equal Split
          </Button> */}
        </Box>

        {/* === Weight Card === */}
        <Card
          sx={{
            p: 3,
            borderRadius: 0,
            boxShadow: "0 3px 6px rgba(0,0,0,0.05)",
            bgcolor: "#fff",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              Trust Attributes weight
            </Typography>
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={handleEditOrReset}
                sx={{
                  borderColor: "#0094FB",
                  color: "#2563EB",
                  textTransform: "none",
                  px: 2,
                }}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                startIcon={<Replay />}
                onClick={handleReset}
                sx={{
                  borderColor: "#0094FB",
                  color: "#2563EB",
                  textTransform: "none",
                  px: 2,
                }}
              >
                Reset
              </Button>
            </Box>
          </Box>

          <LinearProgress
            variant="determinate"
            value={Math.min(totalWeight, 100)}
            sx={{
              height: 10,
              borderRadius: 5,
              mb: 2,
              "& .MuiLinearProgress-bar": {
                bgcolor: totalWeight > 100 ? "#F87171" : "#079C03",
              },
            }}
          />
          <Typography
            variant="body2"
            mb={2}
            color={totalWeight > 100 ? "error" : "#111827"}
          >
            Total Weight: <strong>{totalWeight}%</strong>
          </Typography>

          {ATTRIBUTES.map((attr) => (
            <Box key={attr.label} sx={{ mb: 0.5 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Typography variant="body2" fontWeight={500}>
                  {attr.label}
                </Typography>
                <Typography variant="body2" fontWeight={600} color="#111827">
                  {weights[attr.label]}%
                </Typography>
              </Box>
              <Slider
                value={weights[attr.label]}
                onChange={(_, val) => handleManualChange(attr.label, val)}
                disabled={!isEditable}
                min={0}
                max={100}
                step={1}
                valueLabelDisplay="auto"
                sx={sliderStyles(totalWeight > 100 ? "#F87171" : attr.color)}
              />
            </Box>
          ))}
        </Card>

        {/* === Target Trust Index Card === */}
        <Card
          sx={{
            mt: 3,
            p: 3,
            borderRadius: 0,
            boxShadow: "0 3px 6px rgba(0,0,0,0.05)",
            bgcolor: "#fff",
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} mb={2}>
            Target Trust Index
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Set the target trust index percentage that will be displayed on the Evaluation Dashboard.
            This is the benchmark score your application should aim to achieve.
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <TextField
              type="number"
              value={targetTrustIndex}
              onChange={(e) => {
                const value = parseInt(e.target.value, 10);
                if (!isNaN(value) && value >= 0 && value <= 100) {
                  setTargetTrustIndex(value);
                }
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                inputProps: { min: 0, max: 100 },
              }}
              sx={{
                width: 150,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />
            <Slider
              value={targetTrustIndex}
              onChange={(_, val) => setTargetTrustIndex(val)}
              min={0}
              max={100}
              step={1}
              valueLabelDisplay="auto"
              sx={{
                flex: 1,
                maxWidth: 400,
                color: "#0094FB",
                "& .MuiSlider-thumb": {
                  backgroundColor: "#fff",
                  border: "2px solid #0094FB",
                },
              }}
            />
            <Typography
              variant="h5"
              fontWeight={700}
              color="#0094FB"
              sx={{ minWidth: 80, textAlign: "right" }}
            >
              {targetTrustIndex}%
            </Typography>
          </Box>
        </Card>

        {/* === Footer === */}
        <Box display="flex" justifyContent="flex-end" mt={4}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleNext}
            disabled={totalWeight !== 100}
            sx={{
              bgcolor: "#0094FB",
              textTransform: "none",
              borderRadius: 2,
              px: 4,
              py: 1,
              fontWeight: 600,
              boxShadow: "0 3px 8px rgba(59,130,246,0.35)",
              "&:hover": { bgcolor: "#2563EB" },
            }}
          >
            Go to Identify Risks →
          </Button>
        </Box>

        {/* Loading Overlay */}
        <LoadingOverlay open={loading} message="Generating Weights..." />
      </Box>
    </Box>
  );
};

export default TrustIndexSettingsPage;

// import React, { useState } from "react";
// import {
//   Box,
//   Typography,
//   Grid,
//   Slider,
//   Button,
//   Card,
//   LinearProgress,
//   Chip,
//   CircularProgress,
//   Alert,
// } from "@mui/material";
// import { useNavigate, useLocation } from "react-router-dom";
// import axios from "axios";

// const ATTRIBUTES = [
//   { label: "Accuracy", color: "#1CE017" },
//   { label: "Fairness", color: "#9ED2FF" },
//   { label: "Privacy", color: "#EC3AD1" },
//   { label: "Security", color: "#FFD54F" },
//   { label: "Robustness", color: "#FF7B2E" },
//   { label: "Explainability", color: "#6FFFB0" },
// ];

// const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
// const SIDEBAR_WIDTH = 0;

// // Custom slider styling
// const sliderStyles = (color, isDisabled = false, isError = false) => ({
//   height: 10,
//   borderRadius: 5,
//   "& .MuiSlider-track": {
//     border: "none",
//     height: 10,
//     borderRadius: 5,
//     backgroundColor: isError ? "#F87171" : color,
//   },
//   "& .MuiSlider-rail": {
//     opacity: 0.3,
//     height: 10,
//     borderRadius: 5,
//     backgroundColor: "#D1D5DB",
//   },
//   "& .MuiSlider-thumb": {
//     height: 18,
//     width: 18,
//     backgroundColor: "#fff",
//     border: `2px solid ${isError ? "#F87171" : color}`,
//     "&:hover": {
//       boxShadow: `0 0 0 8px ${
//         isError ? "rgba(248,113,113,0.16)" : `${color}33`
//       }`,
//     },
//   },
//   ...(isDisabled && {
//     "& .MuiSlider-thumb": { borderColor: "#9CA3AF" },
//     "& .MuiSlider-track": { backgroundColor: "#9CA3AF" },
//   }),
// });

// const TrustIndexSettingsPage = () => {
//   const navigate = useNavigate();
//   const location = useLocation();

//   const {
//     applicationName,
//     domain,
//     archetype,
//     riskCode,
//     riskLevel,
//     description,
//     modifiers = [],
//   } = location.state || {};

//   const [aiWeights, setAiWeights] = useState(
//     ATTRIBUTES.reduce((acc, attr) => ({ ...acc, [attr.label]: 0 }), {})
//   );
//   const [manualWeights, setManualWeights] = useState(
//     ATTRIBUTES.reduce((acc, attr) => ({ ...acc, [attr.label]: 0 }), {})
//   );

//   const [loading, setLoading] = useState(false);
//   const [isEditable, setIsEditable] = useState(false);
//   const [successMessage, setSuccessMessage] = useState(null);
//   const [error, setError] = useState(null);

//   const totalAI = Object.values(aiWeights).reduce((sum, v) => sum + v, 0);
//   const totalManual = Object.values(manualWeights).reduce((sum, v) => sum + v, 0);

//   // Backend call to get AI recommendations
//   const handleGenerateWeight = async () => {
//     setLoading(true);
//     setError(null);
//     setSuccessMessage(null);

//     try {
//       const response = await axios.post(`${baseUrl}/trust-index-settings`, {
//         applicationSummary: {
//           applicationName,
//           archetypeCode: archetype,
//           riskTier: riskLevel,
//           domain,
//           description,
//           modifiers,
//         },
//       });

//       if (response.data.success && response.data.data?.recommendedWeights) {
//         const newWeights = response.data.data.recommendedWeights;
//         setAiWeights(newWeights);
//         setManualWeights(newWeights);
//         setSuccessMessage("✅ Weights generated successfully via LLM!");
//       } else {
//         setError("Failed to generate AI recommendations.");
//       }
//     } catch (err) {
//       console.error("Error generating AI recommendations:", err);
//       setError("Failed to generate AI recommendations. Check console for details.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleEqualSplit = () => {
//     const equal = Math.floor(100 / ATTRIBUTES.length);
//     const remainder = 100 - equal * ATTRIBUTES.length;
//     const newWeights = ATTRIBUTES.reduce((acc, attr, i) => {
//       acc[attr.label] = i === 0 ? equal + remainder : equal;
//       return acc;
//     }, {});
//     setAiWeights(newWeights);
//     setManualWeights(newWeights);
//   };

//   const handleManualChange = (attr, value) => {
//     if (!isEditable) return;
//     setManualWeights((prev) => ({ ...prev, [attr]: value }));
//   };

//   const handleEditOrReset = () => {
//     if (isEditable) {
//       setManualWeights(aiWeights);
//       setIsEditable(false);
//     } else {
//       setIsEditable(true);
//     }
//   };

//   const handleNext = () => {
//     navigate("/risk-identification", {
//       state: {
//         applicationName,
//         domain,
//         archetype,
//         riskCode,
//         riskLevel,
//         description,
//         modifiers,
//         trustWeights: manualWeights,
//       },
//     });
//   };

//   return (
//     <Box
//       sx={{
//         flex: 1,
//         bgcolor: "#f3f2f5ff",
//         minHeight: "100vh",
//         ml: { xs: 0, md: `${SIDEBAR_WIDTH}px` },
//         fontFamily: "'Inter', sans-serif",
//       }}
//     >
//       {/* === Sticky Header === */}
//       <Box
//         sx={{
//           position: "sticky",
//           top: 50,
//           zIndex: 100,
//           backgroundColor: "#f3f2f5ff",
//           py: 2.5,
//           px: 2,
//         }}
//       >
//         <Typography variant="h5" fontWeight={600} color="#111827">
//           Trust Dimensions & Weight Assignment
//         </Typography>
//         <Typography variant="body2" color="text.secondary">
//           Define evaluation priorities based on risk profile and regulatory requirements.
//         </Typography>
//       </Box>

//       {/* === Main Page Content === */}
//       <Box sx={{ p: 4, pt: 2 }}>
//         {error && (
//           <Alert severity="error" sx={{ mb: 3 }}>
//             {error}
//           </Alert>
//         )}
//         {successMessage && (
//           <Alert severity="success" sx={{ mb: 3 }}>
//             {successMessage}
//           </Alert>
//         )}

//         {/* === Card 1: Inputs from Previous Stage === */}
//         <Card
//           sx={{
//             mb: 3,
//             p: 3,
//             borderRadius: 3,
//             boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
//             bgcolor: "#fff",
//           }}
//         >
//           <Typography variant="subtitle1" fontWeight={700} mb={2}>
//             📄 Inputs from Previous Stage
//           </Typography>

//           <Grid container spacing={3} alignItems="center">
//             <Grid item xs={12} md={3}>
//               <Typography variant="body2" color="#6b7280">
//                 Application
//               </Typography>
//               <Typography variant="subtitle2" fontWeight={500}>
//                 {applicationName || "Not specified"}
//               </Typography>
//             </Grid>

//             <Grid item xs={12} md={3}>
//               <Typography variant="body2" color="#6b7280">
//                 Archetype Code
//               </Typography>
//               <Typography variant="subtitle2" fontWeight={500}>
//                 {archetype || "Not specified"}
//               </Typography>
//             </Grid>

//             <Grid item xs={12} md={3}>
//               <Typography variant="body2" color="#6b7280">
//                 Risk Tier
//               </Typography>
//               <Typography
//                 variant="subtitle2"
//                 fontWeight={600}
//                 color={
//                   riskLevel?.toLowerCase() === "high"
//                     ? "#DC2626"
//                     : riskLevel?.toLowerCase() === "medium"
//                     ? "#F97316"
//                     : "#16A34A"
//                 }
//               >
//                 {riskLevel || "Not specified"}
//               </Typography>
//             </Grid>

//             <Grid item xs={12} md={3}>
//               <Typography variant="body2" color="#6b7280">
//                 Domain
//               </Typography>
//               <Typography variant="subtitle2" fontWeight={500}>
//                 {domain || "Not specified"}
//               </Typography>
//             </Grid>

//             <Grid item xs={12}>
//               <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
//                 <Typography
//                   variant="subtitle2"
//                   fontWeight={700}
//                   color="#111827"
//                   sx={{ display: "flex", alignItems: "center" }}
//                 >
//                   Modifiers:
//                 </Typography>

//                 <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
//                   {modifiers && modifiers.length > 0 ? (
//                     modifiers.map((mod) => (
//                       <Box
//                         key={mod}
//                         sx={{
//                           px: 1.5,
//                           py: 0.3,
//                           borderRadius: "8px",
//                           backgroundColor:
//                             mod === "PII"
//                               ? "#E8F1FF"
//                               : mod === "RAG"
//                               ? "#DCFCE7"
//                               : mod === "ENT"
//                               ? "#DBEAFE"
//                               : "#F3F4F6",
//                           color:
//                             mod === "PII"
//                               ? "#0056B3"
//                               : mod === "RAG"
//                               ? "#166534"
//                               : mod === "ENT"
//                               ? "#1E3A8A"
//                               : "#374151",
//                           fontWeight: 600,
//                           fontSize: "0.85rem",
//                           display: "flex",
//                           alignItems: "center",
//                           justifyContent: "center",
//                           textTransform: "uppercase",
//                           boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)",
//                         }}
//                       >
//                         {mod}
//                       </Box>
//                     ))
//                   ) : (
//                     <Typography variant="subtitle2" fontWeight={500}>
//                       Not specified
//                     </Typography>
//                   )}
//                 </Box>
//               </Box>
//             </Grid>
//           </Grid>
//         </Card>

//         {/* === Buttons Section === */}
//         <Box display="flex" justifyContent="flex-start" gap={2} mb={3}>
//           <Button
//             variant="contained"
//             onClick={handleGenerateWeight}
//             startIcon={loading ? <CircularProgress size={16} /> : null}
//             disabled={loading}
//             sx={{
//               bgcolor: "#3B82F6",
//               textTransform: "none",
//               borderRadius: 2,
//               px: 3,
//               boxShadow: "0 2px 5px rgba(59,130,246,0.3)",
//               "&:hover": { bgcolor: "#2563EB" },
//             }}
//           >
//             {loading ? "Generating..." : "Generate Weight"}
//           </Button>
//           <Button
//             variant="outlined"
//             onClick={handleEqualSplit}
//             sx={{
//               borderColor: "#93C5FD",
//               color: "#2563EB",
//               textTransform: "none",
//               borderRadius: 2,
//               px: 3,
//               "&:hover": { borderColor: "#2563EB", background: "#EFF6FF" },
//             }}
//           >
//             Equal Split
//           </Button>
//         </Box>

//         {/* === Weight Distribution Cards === */}
//         <Grid container spacing={3}>
//           <Grid item xs={12} md={6}>
//             <Card
//               sx={{
//                 p: 3,
//                 borderRadius: 3,
//                 boxShadow: "0 3px 6px rgba(0,0,0,0.05)",
//                 bgcolor: "#fff",
//               }}
//             >
//               <Typography variant="subtitle1" fontWeight={600} mb={2}>
//                 Weight Generated via LLM
//               </Typography>
//               <LinearProgress
//                 variant="determinate"
//                 value={totalAI}
//                 sx={{
//                   height: 10,
//                   borderRadius: 5,
//                   mb: 1,
//                   "& .MuiLinearProgress-bar": {
//                     bgcolor: "#22C55E",
//                     borderRadius: 5,
//                   },
//                 }}
//               />
//               <Typography variant="body2" mb={2}>
//                 Total Weight: <strong>{totalAI}%</strong>
//               </Typography>

//               {ATTRIBUTES.map((attr) => (
//                 <Box key={attr.label} mb={0.5}>
//                   <Typography variant="body2" mb={0.5}>
//                     {attr.label}
//                   </Typography>
//                   <Slider
//                     value={aiWeights[attr.label]}
//                     disabled
//                     min={0}
//                     max={100}
//                     step={1}
//                     valueLabelDisplay="auto"
//                     sx={sliderStyles(attr.color, true)}
//                   />
//                 </Box>
//               ))}
//             </Card>
//           </Grid>

//           <Grid item xs={12} md={6}>
//             <Card
//               sx={{
//                 p: 3,
//                 borderRadius: 3,
//                 boxShadow: "0 3px 6px rgba(0,0,0,0.05)",
//                 bgcolor: "#fff",
//               }}
//             >
//               <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
//                 <Typography variant="subtitle1" fontWeight={600}>
//                   Manually Set Weight
//                 </Typography>
//                 <Button
//                   variant="text"
//                   onClick={handleEditOrReset}
//                   sx={{
//                     color: isEditable ? "#DC2626" : "#2563EB",
//                     textTransform: "none",
//                     fontWeight: 600,
//                   }}
//                 >
//                   {isEditable ? "Reset" : "Edit"}
//                 </Button>
//               </Box>

//               <LinearProgress
//                 variant="determinate"
//                 value={Math.min(totalManual, 100)}
//                 sx={{
//                   height: 10,
//                   borderRadius: 5,
//                   mb: 1,
//                   "& .MuiLinearProgress-bar": {
//                     bgcolor: totalManual > 100 ? "#F87171" : "#22C55E",
//                     borderRadius: 5,
//                   },
//                 }}
//               />
//               <Typography
//                 variant="body2"
//                 mb={1}
//                 color={totalManual > 100 ? "error" : "#111827"}
//               >
//                 Total Weight: <strong>{totalManual}%</strong>
//               </Typography>

//               {totalManual > 100 && (
//                 <Typography variant="caption" color="error" mb={2} display="block">
//                   ⚠️ Total weight should not exceed 100%
//                 </Typography>
//               )}

//               {ATTRIBUTES.map((attr) => (
//                 <Box key={attr.label} mb={0.5}>
//                   <Typography variant="body2" mb={0.5}>
//                     {attr.label}
//                   </Typography>
//                   <Slider
//                     value={manualWeights[attr.label]}
//                     onChange={(_, val) => handleManualChange(attr.label, val)}
//                     disabled={!isEditable}
//                     min={0}
//                     max={100}
//                     step={1}
//                     valueLabelDisplay="auto"
//                     sx={sliderStyles(
//                       totalManual > 100 ? "#F87171" : attr.color,
//                       !isEditable,
//                       totalManual > 100
//                     )}
//                   />
//                 </Box>
//               ))}
//             </Card>
//           </Grid>
//         </Grid>

//         {/* === Next Button === */}
//         <Box display="flex" justifyContent="flex-end" mt={4}>
//           <Button
//             variant="contained"
//             color="primary"
//             onClick={handleNext}
//             disabled={totalManual > 100 || totalManual < 100}
//             sx={{
//               bgcolor: "#3B82F6",
//               textTransform: "none",
//               borderRadius: 3,
//               px: 4,
//               py: 1,
//               fontWeight: 600,
//               boxShadow: "0 3px 8px rgba(59,130,246,0.35)",
//               "&:hover": { bgcolor: "#2563EB" },
//             }}
//           >
//             Next →
//           </Button>
//         </Box>
//       </Box>
//     </Box>
//   );
// };

// export default TrustIndexSettingsPage;

// import React, { useState } from "react";
// import {
//   Box,
//   Typography,
//   Grid,
//   Slider,
//   Button,
//   Card,
//   LinearProgress,
//   Chip,
//   CircularProgress,
// } from "@mui/material";
// import { useNavigate, useLocation } from "react-router-dom";
// import axios from "axios";

// const ATTRIBUTES = [
//   { label: "Accuracy", color: "#1CE017" },
//   { label: "Fairness", color: "#9ED2FF" },
//   { label: "Privacy", color: "#EC3AD1" },
//   { label: "Security", color: "#FFD54F" },
//   { label: "Robustness", color: "#FF7B2E" },
//   { label: "Explainability", color: "#6FFFB0" },
// ];

// const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
// const SIDEBAR_WIDTH = 0;

// /**
//  * Custom slider style — matches LinearProgress height & visual theme
//  */
// const sliderStyles = (color, isDisabled = false, isError = false) => ({
//   height: 10,
//   borderRadius: 5,
//   "& .MuiSlider-track": {
//     border: "none",
//     height: 10,
//     borderRadius: 5,
//     backgroundColor: isError ? "#F87171" : color,
//   },
//   "& .MuiSlider-rail": {
//     opacity: 0.3,
//     height: 10,
//     borderRadius: 5,
//     backgroundColor: "#D1D5DB",
//   },
//   "& .MuiSlider-thumb": {
//     height: 18,
//     width: 18,
//     backgroundColor: "#fff",
//     border: `2px solid ${isError ? "#F87171" : color}`,
//     "&:hover": {
//       boxShadow: `0 0 0 8px ${
//         isError ? "rgba(248,113,113,0.16)" : `${color}33`
//       }`,
//     },
//   },
//   "& .MuiSlider-valueLabel": {
//     fontSize: 12,
//     fontWeight: 600,
//   },
//   ...(isDisabled && {
//     "& .MuiSlider-thumb": { borderColor: "#9CA3AF" },
//     "& .MuiSlider-track": { backgroundColor: "#9CA3AF" },
//   }),
// });

// const TrustIndexSettingsPage = () => {
//   const navigate = useNavigate();
//   const location = useLocation();

//   const {
//     applicationName,
//     domain,
//     archetype,
//     riskCode,
//     riskLevel,
//     description,
//     modifiers = [],
//   } = location.state || {};

//   const [aiWeights, setAiWeights] = useState(
//     ATTRIBUTES.reduce((acc, attr) => ({ ...acc, [attr.label]: 0 }), {})
//   );

//   const [manualWeights, setManualWeights] = useState(
//     ATTRIBUTES.reduce((acc, attr) => ({ ...acc, [attr.label]: 0 }), {})
//   );

//   const [loading, setLoading] = useState(false);
//   const [isEditable, setIsEditable] = useState(false);
//   const [successMessage, setSuccessMessage] = useState(null);
//   const [error, setError] = useState(null);

//   const totalAI = Object.values(aiWeights).reduce((sum, v) => sum + v, 0);
//   const totalManual = Object.values(manualWeights).reduce((sum, v) => sum + v, 0);

//   // Backend call to get AI recommendations
//   const handleGenerateWeight = async () => {
//     setLoading(true);
//     setError(null);
//     setSuccessMessage(null);

//     try {
//       const response = await axios.post(`${baseUrl}/trust-index-settings`, {
//         applicationSummary: {
//           applicationName,
//           archetypeCode: archetype,
//           riskTier: riskLevel,
//           domain,
//           description,
//           modifiers,
//         },
//       });

//       if (response.data.success && response.data.data?.recommendedWeights) {
//         const newWeights = response.data.data.recommendedWeights;
//         setAiWeights(newWeights);
//         setManualWeights(newWeights);
//         setSuccessMessage("✅ Weights generated successfully via LLM!");
//       } else {
//         setError("Failed to generate AI recommendations.");
//       }
//     } catch (err) {
//       console.error("Error generating AI recommendations:", err);
//       setError("Failed to generate AI recommendations. Check console for details.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleEqualSplit = () => {
//     const equal = Math.floor(100 / ATTRIBUTES.length);
//     const remainder = 100 - equal * ATTRIBUTES.length;
//     const newWeights = ATTRIBUTES.reduce((acc, attr, i) => {
//       acc[attr.label] = i === 0 ? equal + remainder : equal;
//       return acc;
//     }, {});
//     setAiWeights(newWeights);
//     setManualWeights(newWeights);
//   };

//   const handleManualChange = (attr, value) => {
//     if (!isEditable) return;
//     setManualWeights((prev) => ({ ...prev, [attr]: value }));
//   };

//   /** Toggle Edit ↔ Reset */
//   const handleEditOrReset = () => {
//     if (isEditable) {
//       // Reset back to AI-generated values
//       setManualWeights(aiWeights);
//       setIsEditable(false);
//     } else {
//       // Enable editing
//       setIsEditable(true);
//     }
//   };

//   const handleNext = () => {
//     navigate("/risk-identification", {
//       state: {
//         applicationName,
//         domain,
//         archetype,
//         riskCode,
//         riskLevel,
//         description,
//          modifiers,
//         // trustWeights: manualWeights,
//       },
//     });
//   };

//   return (
//       <Box
//         // sx={{
//         //   flex: 1,
//         //   pt: 0.5,  // keep only top padding
//         //   pr: 1,  // right spacing
//         //   pb: 1,  // bottom spacing
//         //   pl: 5,  // smaller left padding (aligns content with sidebar)
//         //   bgcolor: "#F9F8F7",
//         //   minHeight: "100vh",
//         //   ml: { xs: 0, md: `${SIDEBAR_WIDTH}px` },
//         //   fontFamily: "'Inter', sans-serif",
//         // }}
//          sx={{
//             flex: 1,
//             bgcolor: "#f3f2f5ff", // same as TrustIndex page background
//             minHeight: "100vh",
//             ml: { xs: 0, md: `${SIDEBAR_WIDTH}px` },
//             fontFamily: "'Inter', sans-serif",
//           }}
//       >
//       {/* Sticky Header */}
//       <Box
//         sx={{
//           position: "sticky",
//           top: 50, // 👈 keeps it below your AppHeader (adjust if needed)
//           zIndex: 100,
//           backgroundColor: "#F9F8F7",
//           pb: 2.5,
//           pt: 2,
//           // borderBottom: "1px solid #E5E7EB",
//         }}
//       >
//         <Typography variant="h5" fontWeight={600} color="#111827">
//           Trust Dimensions & Weight Assignment
//         </Typography>
//         <Typography variant="body2" color="text.secondary">
//           Define evaluation priorities based on risk profile and regulatory requirements.
//         </Typography>
//         {/* <Box sx={{ mt: 1, borderBottom: "1px solid #E5E7EB" }}></Box>  */}
//       </Box>
//       {/* <Box sx={{ mt: 5 }}></Box> */}

//       {/* <Typography variant="h5" fontWeight={600} color="#111827" gutterBottom>
//         Trust Dimensions & Weight Assignment
//       </Typography>
//       <Typography variant="body2" color="text.secondary" mb={3}>
//         Define evaluation priorities based on risk profile and regulatory requirements.
//       </Typography>  */}

//       {/* Inputs from Previous Stage */}
//       <Card
//         sx={{
//           // mt: -0.5,
//           mb: 3,
//           p: 3,
//           borderRadius: 3,
//           boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
//           bgcolor: "#fff",
//         }}
//       >
//         <Typography variant="subtitle1" fontWeight={600} mb={1}>
//           Inputs from Previous Stage
//         </Typography>
//         <Grid container spacing={2}>
//           <Grid item xs={6} md={3}>
//             <Typography variant="body2" color="#6b7280">
//               Application
//             </Typography>
//             <Typography variant="subtitle2" fontWeight={500}>
//               {applicationName || "N/A"}
//             </Typography>
//           </Grid>
//           <Grid item xs={6} md={3}>
//             <Typography variant="body2" color="#6b7280">
//               Archetype Code
//             </Typography>
//             <Typography variant="subtitle2" fontWeight={500}>
//               {archetype || "N/A"}
//             </Typography>
//           </Grid>
//           <Grid item xs={6} md={3}>
//             <Typography variant="body2" color="#6b7280">
//               Risk Tier
//             </Typography>
//             <Typography
//               variant="subtitle2"
//               fontWeight={600}
//               color={riskLevel?.toLowerCase() === "high" ? "#DC2626" : "#111827"}
//             >
//               {riskLevel || "N/A"}
//             </Typography>
//           </Grid>
//           <Grid item xs={6} md={3}>
//             <Typography variant="body2" color="#6b7280">
//               Domain
//             </Typography>
//             <Typography variant="subtitle2" fontWeight={500}>
//               {domain || "N/A"}
//             </Typography>
//           </Grid>
//         </Grid>

//         {/* Modifiers section */}
//         {/* <Box mt={2}>
//           <Typography variant="body2" color="#6b7280" mb={1}>
//             Modifiers:
//           </Typography>
//           <Chip
//             label="RAG"
//             sx={{ mr: 1, bgcolor: "#E0F2FE", color: "#0284C7", fontWeight: 500 }}
//           />
//           <Chip
//             label="ENT"
//             sx={{ mr: 1, bgcolor: "#E0FBE2", color: "#15803D", fontWeight: 500 }}
//           />
//           <Chip
//             label="PII"
//             sx={{ bgcolor: "#FEE2E2", color: "#B91C1C", fontWeight: 500 }}
//           />
//         </Box> */}
//         {/* Modifiers section */}
//           {modifiers.length > 0 && (
//             <Box mt={2}>
//               <Typography variant="body2" color="#6b7280" mb={1}>
//                 Modifiers:
//               </Typography>
//               {modifiers.map((mod) => (
//                 <Chip
//                   key={mod}
//                   label={mod}
//                   sx={{
//                     mr: 1,
//                     mb: 1,
//                     bgcolor:
//                       mod === "PII"
//                         ? "#FEE2E2"
//                         : mod === "PHI"
//                         ? "#FEF3C7"
//                         : mod === "Financial"
//                         ? "#DCFCE7"
//                         : mod === "Confidential"
//                         ? "#E0F2FE"
//                         : "#F3F4F6",
//                     color:
//                       mod === "PII"
//                         ? "#B91C1C"
//                         : mod === "PHI"
//                         ? "#92400E"
//                         : mod === "Financial"
//                         ? "#166534"
//                         : mod === "Confidential"
//                         ? "#1E3A8A"
//                         : "#374151",
//                     fontWeight: 500,
//                   }}
//                 />
//               ))}
//             </Box>
//           )}

//       </Card>

//       {/* Buttons */}
//       <Box display="flex" justifyContent="flex-start" gap={2} mb={3}>
//         <Button
//           variant="contained"
//           onClick={handleGenerateWeight}
//           startIcon={loading ? <CircularProgress size={16} /> : null}
//           disabled={loading}
//           sx={{
//             bgcolor: "#3B82F6",
//             textTransform: "none",
//             borderRadius: 2,
//             px: 3,
//             boxShadow: "0 2px 5px rgba(59,130,246,0.3)",
//             "&:hover": { bgcolor: "#2563EB" },
//           }}
//         >
//           {loading ? "Generating..." : "Generate Weight"}
//         </Button>
//         <Button
//           variant="outlined"
//           onClick={handleEqualSplit}
//           sx={{
//             borderColor: "#93C5FD",
//             color: "#2563EB",
//             textTransform: "none",
//             borderRadius: 2,
//             px: 3,
//             "&:hover": { borderColor: "#2563EB", background: "#EFF6FF" },
//           }}
//         >
//           Equal Split
//         </Button>
//       </Box>

//       {/* Weight Cards */}
//       <Grid container spacing={3}>
//         {/* LLM Generated */}
//         <Grid item xs={12} md={6}>
//           <Card
//             sx={{
//               p: 3,
//               borderRadius: 3,
//               boxShadow: "0 3px 6px rgba(0,0,0,0.05)",
//               bgcolor: "#fff",
//             }}
//           >
//             <Typography variant="subtitle1" fontWeight={600} mb={2}>
//               Weight Generated via LLM
//             </Typography>

//             <LinearProgress
//               variant="determinate"
//               value={totalAI}
//               sx={{
//                 height: 10,
//                 borderRadius: 5,
//                 mb: 1,
//                 "& .MuiLinearProgress-bar": {
//                   bgcolor: "#22C55E",
//                   borderRadius: 5,
//                 },
//               }}
//             />
//             <Typography variant="body2" mb={2}>
//               Total Weight: <strong>{totalAI}%</strong>
//             </Typography>

//             {ATTRIBUTES.map((attr) => (
//               <Box key={attr.label} mb={0.5}>
//                 <Typography variant="body2" mb={0.5}>
//                   {attr.label}
//                 </Typography>
//                 <Slider
//                   value={aiWeights[attr.label]}
//                   disabled
//                   min={0}
//                   max={100}
//                   step={1}
//                   valueLabelDisplay="auto"
//                   sx={sliderStyles(attr.color, true)}
//                 />
//               </Box>
//             ))}
//           </Card>
//         </Grid>

//         {/* Manually Set */}
//         <Grid item xs={12} md={6}>
//           <Card
//             sx={{
//               p: 3,
//               borderRadius: 3,
//               boxShadow: "0 3px 6px rgba(0,0,0,0.05)",
//               bgcolor: "#fff",
//             }}
//           >
//             <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
//               <Typography variant="subtitle1" fontWeight={600}>
//                 Manually Set Weight
//               </Typography>
//               <Button
//                 variant="text"
//                 onClick={handleEditOrReset}
//                 sx={{
//                   color: isEditable ? "#DC2626" : "#2563EB",
//                   textTransform: "none",
//                   fontWeight: 600,
//                 }}
//               >
//                 {isEditable ? "Reset" : "Edit"}
//               </Button>
//             </Box>

//             <LinearProgress
//               variant="determinate"
//               value={Math.min(totalManual, 100)}
//               sx={{
//                 height: 10,
//                 borderRadius: 5,
//                 mb: 1,
//                 "& .MuiLinearProgress-bar": {
//                   bgcolor: totalManual > 100 ? "#F87171" : "#22C55E",
//                   borderRadius: 5,
//                 },
//               }}
//             />
//             <Typography
//               variant="body2"
//               mb={1}
//               color={totalManual > 100 ? "error" : "#111827"}
//             >
//               Total Weight: <strong>{totalManual}%</strong>
//             </Typography>
//             {totalManual > 100 && (
//               <Typography variant="caption" color="error" mb={2} display="block">
//                 ⚠️ Total weight should not exceed 100%
//               </Typography>
//             )}

//             {ATTRIBUTES.map((attr) => (
//               <Box key={attr.label} mb={0.5}>
//                 <Typography variant="body2" mb={0.5}>
//                   {attr.label}
//                 </Typography>
//                 <Slider
//                   value={manualWeights[attr.label]}
//                   onChange={(_, val) => handleManualChange(attr.label, val)}
//                   disabled={!isEditable}
//                   min={0}
//                   max={100}
//                   step={1}
//                   valueLabelDisplay="auto"
//                   sx={sliderStyles(
//                     totalManual > 100 ? "#F87171" : attr.color,
//                     !isEditable,
//                     totalManual > 100
//                   )}
//                 />
//               </Box>
//             ))}
//           </Card>
//         </Grid>
//       </Grid>

//       {/* Next Button */}
//       <Box display="flex" justifyContent="flex-end" mt={4}>
//         <Button
//           variant="contained"
//           color="primary"
//           onClick={handleNext}
//           disabled={totalManual > 100 || totalManual < 100}
//           sx={{
//             bgcolor: "#3B82F6",
//             textTransform: "none",
//             borderRadius: 3,
//             px: 4,
//             py: 1,
//             fontWeight: 600,
//             boxShadow: "0 3px 8px rgba(59,130,246,0.35)",
//             "&:hover": { bgcolor: "#2563EB" },
//           }}
//         >
//           Next →
//         </Button>
//       </Box>
//     </Box>
//   );
// };

// export default TrustIndexSettingsPage;

// import React, { useState } from "react";
// import {
//   Box,
//   Typography,
//   Grid,
//   Slider,
//   Button,
//   Card,
//   LinearProgress,
//   Chip,
//   CircularProgress,
// } from "@mui/material";
// import { useNavigate, useLocation } from "react-router-dom";
// import axios from "axios";

// const ATTRIBUTES = [
//   { label: "Accuracy", color: "#1CE017" },
//   { label: "Fairness", color: "#9ED2FF" },
//   { label: "Privacy", color: "#EC3AD1" },
//   { label: "Security", color: "#FFD54F" },
//   { label: "Robustness", color: "#FF7B2E" },
//   { label: "Explainability", color: "#6FFFB0" },
// ];

// const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
// const SIDEBAR_WIDTH = 64;

// /**
//  * Custom slider style — matches LinearProgress height & visual theme
//  */
// const sliderStyles = (color, isDisabled = false, isError = false) => ({
//   height: 10, // Match LinearProgress bar height
//   borderRadius: 5,
//   "& .MuiSlider-track": {
//     border: "none",
//     height: 10,
//     borderRadius: 5,
//     backgroundColor: isError ? "#F87171" : color,
//   },
//   "& .MuiSlider-rail": {
//     opacity: 0.3,
//     height: 10,
//     borderRadius: 5,
//     backgroundColor: "#D1D5DB",
//   },
//   "& .MuiSlider-thumb": {
//     height: 18,
//     width: 18,
//     backgroundColor: "#fff",
//     border: `2px solid ${isError ? "#F87171" : color}`,
//     "&:hover": {
//       boxShadow: `0 0 0 8px ${
//         isError ? "rgba(248,113,113,0.16)" : `${color}33`
//       }`,
//     },
//   },
//   "& .MuiSlider-valueLabel": {
//     fontSize: 12,
//     fontWeight: 600,
//   },
//   ...(isDisabled && {
//     "& .MuiSlider-thumb": { borderColor: "#9CA3AF" },
//     "& .MuiSlider-track": { backgroundColor: "#9CA3AF" },
//   }),
// });

// const TrustIndexSettingsPage = () => {
//   const navigate = useNavigate();
//   const location = useLocation();

//   const {
//     applicationName,
//     domain,
//     archetype,
//     riskCode,
//     riskLevel,
//     description,
//   } = location.state || {};

//   const [aiWeights, setAiWeights] = useState(
//     ATTRIBUTES.reduce((acc, attr) => ({ ...acc, [attr.label]: 0 }), {})
//   );

//   const [manualWeights, setManualWeights] = useState(
//     ATTRIBUTES.reduce((acc, attr) => ({ ...acc, [attr.label]: 0 }), {})
//   );

//   const [loading, setLoading] = useState(false);
//   const [isEditable, setIsEditable] = useState(false);
//   const [successMessage, setSuccessMessage] = useState(null);
//   const [error, setError] = useState(null);

//   const totalAI = Object.values(aiWeights).reduce((sum, v) => sum + v, 0);
//   const totalManual = Object.values(manualWeights).reduce((sum, v) => sum + v, 0);

//   // Backend call to get AI recommendations
//   const handleGenerateWeight = async () => {
//     setLoading(true);
//     setError(null);
//     setSuccessMessage(null);

//     try {
//       const response = await axios.post(`${baseUrl}/trust-index-settings`, {
//         applicationSummary: {
//           applicationName,
//           archetypeCode: archetype,
//           riskTier: riskLevel,
//           domain,
//           description,
//         },
//       });

//       if (response.data.success && response.data.data?.recommendedWeights) {
//         const newWeights = response.data.data.recommendedWeights;
//         setAiWeights(newWeights);
//         setManualWeights(newWeights);
//         setSuccessMessage("✅ Weights generated successfully via LLM!");
//       } else {
//         setError("Failed to generate AI recommendations.");
//       }
//     } catch (err) {
//       console.error("Error generating AI recommendations:", err);
//       setError("Failed to generate AI recommendations. Check console for details.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleEqualSplit = () => {
//     const equal = Math.floor(100 / ATTRIBUTES.length);
//     const remainder = 100 - equal * ATTRIBUTES.length;
//     const newWeights = ATTRIBUTES.reduce((acc, attr, i) => {
//       acc[attr.label] = i === 0 ? equal + remainder : equal;
//       return acc;
//     }, {});
//     setAiWeights(newWeights);
//     setManualWeights(newWeights);
//   };

//   const handleManualChange = (attr, value) => {
//     if (!isEditable) return;
//     setManualWeights((prev) => ({ ...prev, [attr]: value }));
//   };

//   const handleEditClick = () => {
//     setIsEditable(true);
//   };

//   const handleNext = () => {
//     navigate("/risk-identification", {
//       state: {
//         applicationName,
//         domain,
//         archetype,
//         riskCode,
//         riskLevel,
//         description,
//         trustWeights: manualWeights,
//       },
//     });
//   };

//   return (
//     <Box
//       sx={{
//         flex: 1,
//         p: 4,
//         overflow: "auto",
//         bgcolor: "#F9F8F7",
//         minHeight: "100vh",
//         ml: { xs: 0, md: `${SIDEBAR_WIDTH}px` },
//         fontFamily: "'Inter', sans-serif",
//       }}
//     >
//       {/* Header */}
//       <Typography variant="h5" fontWeight={600} color="#111827" gutterBottom>
//         Trust Dimensions & Weight Assignment
//       </Typography>
//       <Typography variant="body2" color="text.secondary" mb={3}>
//         Define evaluation priorities based on risk profile and regulatory requirements.
//       </Typography>

//       {/* Inputs from Previous Stage */}
//       <Card
//         sx={{
//           mb: 3,
//           p: 3,
//           borderRadius: 3,
//           boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
//           bgcolor: "#fff",
//         }}
//       >
//         <Typography variant="subtitle1" fontWeight={600} mb={1}>
//           Inputs from Previous Stage
//         </Typography>
//         <Grid container spacing={2}>
//           <Grid item xs={6} md={3}>
//             <Typography variant="body2" color="#6b7280">
//               Application
//             </Typography>
//             <Typography variant="subtitle2" fontWeight={500}>
//               {applicationName || "N/A"}
//             </Typography>
//           </Grid>
//           <Grid item xs={6} md={3}>
//             <Typography variant="body2" color="#6b7280">
//               Archetype Code
//             </Typography>
//             <Typography variant="subtitle2" fontWeight={500}>
//               {archetype || "N/A"}
//             </Typography>
//           </Grid>
//           <Grid item xs={6} md={3}>
//             <Typography variant="body2" color="#6b7280">
//               Risk Tier
//             </Typography>
//             <Typography
//               variant="subtitle2"
//               fontWeight={600}
//               color={riskLevel?.toLowerCase() === "high" ? "#DC2626" : "#111827"}
//             >
//               {riskLevel || "N/A"}
//             </Typography>
//           </Grid>
//           <Grid item xs={6} md={3}>
//             <Typography variant="body2" color="#6b7280">
//               Domain
//             </Typography>
//             <Typography variant="subtitle2" fontWeight={500}>
//               {domain || "N/A"}
//             </Typography>
//           </Grid>
//         </Grid>

//         {/* Modifiers section */}
//         <Box mt={2}>
//           <Typography variant="body2" color="#6b7280" mb={1}>
//             Modifiers:
//           </Typography>
//           <Chip
//             label="RAG"
//             sx={{ mr: 1, bgcolor: "#E0F2FE", color: "#0284C7", fontWeight: 500 }}
//           />
//           <Chip
//             label="ENT"
//             sx={{ mr: 1, bgcolor: "#E0FBE2", color: "#15803D", fontWeight: 500 }}
//           />
//           <Chip
//             label="PII"
//             sx={{ bgcolor: "#FEE2E2", color: "#B91C1C", fontWeight: 500 }}
//           />
//         </Box>
//       </Card>

//       {/* Buttons */}
//       <Box display="flex" justifyContent="flex-start" gap={2} mb={3}>
//         <Button
//           variant="contained"
//           onClick={handleGenerateWeight}
//           startIcon={loading ? <CircularProgress size={16} /> : null}
//           disabled={loading}
//           sx={{
//             bgcolor: "#3B82F6",
//             textTransform: "none",
//             borderRadius: 2,
//             px: 3,
//             boxShadow: "0 2px 5px rgba(59,130,246,0.3)",
//             "&:hover": { bgcolor: "#2563EB" },
//           }}
//         >
//           {loading ? "Generating..." : "Generate Weight"}
//         </Button>
//         <Button
//           variant="outlined"
//           onClick={handleEqualSplit}
//           sx={{
//             borderColor: "#93C5FD",
//             color: "#2563EB",
//             textTransform: "none",
//             borderRadius: 2,
//             px: 3,
//             "&:hover": { borderColor: "#2563EB", background: "#EFF6FF" },
//           }}
//         >
//           Equal Split
//         </Button>
//       </Box>

//       {/* Weight Cards */}
//       <Grid container spacing={3}>
//         {/* LLM Generated */}
//         <Grid item xs={12} md={6}>
//           <Card
//             sx={{
//               p: 3,
//               borderRadius: 3,
//               boxShadow: "0 3px 6px rgba(0,0,0,0.05)",
//               bgcolor: "#fff",
//             }}
//           >
//             <Typography variant="subtitle1" fontWeight={700} mb={2}>
//               Weight Generated via LLM
//             </Typography>

//             <LinearProgress
//               variant="determinate"
//               value={totalAI}
//               sx={{
//                 height: 10,
//                 borderRadius: 5,
//                 mb: 1,
//                 "& .MuiLinearProgress-bar": {
//                   bgcolor: "#22C55E",
//                   borderRadius: 5,
//                 },
//               }}
//             />
//             <Typography variant="body2" mb={2}>
//               Total Weight: <strong>{totalAI}%</strong>
//             </Typography>

//             {ATTRIBUTES.map((attr) => (
//               <Box key={attr.label} mb={2}>
//                 <Typography variant="body2" mb={0.5}>
//                   {attr.label}
//                 </Typography>
//                 <Slider
//                   value={aiWeights[attr.label]}
//                   disabled
//                   min={0}
//                   max={100}
//                   step={1}
//                   valueLabelDisplay="auto"
//                   sx={sliderStyles(attr.color, true)}
//                 />
//               </Box>
//             ))}
//           </Card>
//         </Grid>

//         {/* Manually Set */}
//         <Grid item xs={12} md={6}>
//           <Card
//             sx={{
//               p: 3,
//               borderRadius: 3,
//               boxShadow: "0 3px 6px rgba(0,0,0,0.05)",
//               bgcolor: "#fff",
//             }}
//           >
//             <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
//               <Typography variant="subtitle1" fontWeight={600}>
//                 Manually Set Weight
//               </Typography>
//               <Button
//                 variant="text"
//                 onClick={handleEditClick}
//                 disabled={isEditable}
//                 sx={{ color: "#2563EB", textTransform: "none", fontWeight: 600 }}
//               >
//                 Edit
//               </Button>
//             </Box>

//             <LinearProgress
//               variant="determinate"
//               value={Math.min(totalManual, 100)}
//               sx={{
//                 height: 10,
//                 borderRadius: 5,
//                 mb: 1,
//                 "& .MuiLinearProgress-bar": {
//                   bgcolor: totalManual > 100 ? "#F87171" : "#22C55E",
//                   borderRadius: 5,
//                 },
//               }}
//             />
//             <Typography
//               variant="body2"
//               mb={1}
//               color={totalManual > 100 ? "error" : "#111827"}
//             >
//               Total Weight: <strong>{totalManual}%</strong>
//             </Typography>
//             {totalManual > 100 && (
//               <Typography variant="caption" color="error" mb={2} display="block">
//                 ⚠️ Total weight should not exceed 100%
//               </Typography>
//             )}

//             {ATTRIBUTES.map((attr) => (
//               <Box key={attr.label} mb={2}>
//                 <Typography variant="body2" mb={0.5}>
//                   {attr.label}
//                 </Typography>
//                 <Slider
//                   value={manualWeights[attr.label]}
//                   onChange={(_, val) => handleManualChange(attr.label, val)}
//                   disabled={!isEditable}
//                   min={0}
//                   max={100}
//                   step={1}
//                   valueLabelDisplay="auto"
//                   sx={sliderStyles(
//                     totalManual > 100 ? "#F87171" : attr.color,
//                     !isEditable,
//                     totalManual > 100
//                   )}
//                 />
//               </Box>
//             ))}
//           </Card>
//         </Grid>
//       </Grid>

//       {/* Next Button */}
//       <Box display="flex" justifyContent="flex-end" mt={4}>
//         <Button
//           variant="contained"
//           color="primary"
//           onClick={handleNext}
//           disabled={totalManual > 100 || totalManual < 100}
//           sx={{
//             bgcolor: "#3B82F6",
//             textTransform: "none",
//             borderRadius: 3,
//             px: 4,
//             py: 1,
//             fontWeight: 600,
//             boxShadow: "0 3px 8px rgba(59,130,246,0.35)",
//             "&:hover": { bgcolor: "#2563EB" },
//           }}
//         >
//           Next →
//         </Button>
//       </Box>
//     </Box>
//   );
// };

// export default TrustIndexSettingsPage;

// import React, { useState } from "react";
// import {
//   Box,
//   Typography,
//   Grid,
//   Slider,
//   Button,
//   Card,
//   LinearProgress,
//   Chip,
//   CircularProgress,
// } from "@mui/material";
// import { useNavigate, useLocation } from "react-router-dom";
// import axios from "axios";

// const ATTRIBUTES = [
//   { label: "Accuracy", color: "#57D38C" },
//   { label: "Fairness", color: "#6EC6FF" },
//   { label: "Privacy", color: "#F48FB1" },
//   { label: "Security", color: "#FFD54F" },
//   { label: "Robustness", color: "#FFB74D" },
//   { label: "Explainability", color: "#80DEEA" },
// ];

// const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
// const SIDEBAR_WIDTH = 64;

// const TrustIndexSettingsPage = () => {
//   const navigate = useNavigate();
//   const location = useLocation();

//   const {
//     applicationName,
//     domain,
//     archetype,
//     riskCode,
//     riskLevel,
//     description,
//   } = location.state || {};

//   const [aiWeights, setAiWeights] = useState(
//     ATTRIBUTES.reduce((acc, attr) => ({ ...acc, [attr.label]: 0 }), {})
//   );

//   const [manualWeights, setManualWeights] = useState(
//     ATTRIBUTES.reduce((acc, attr) => ({ ...acc, [attr.label]: 0 }), {})
//   );

//   const [loading, setLoading] = useState(false);
//   const [isEditable, setIsEditable] = useState(false);
//   const [successMessage, setSuccessMessage] = useState(null);
//   const [error, setError] = useState(null);

//   const totalAI = Object.values(aiWeights).reduce((sum, v) => sum + v, 0);
//   const totalManual = Object.values(manualWeights).reduce((sum, v) => sum + v, 0);

//   // Backend call to get AI recommendations
//   const handleGenerateWeight = async () => {
//     setLoading(true);
//     setError(null);
//     setSuccessMessage(null);

//     try {
//       const response = await axios.post(`${baseUrl}/trust-index-settings`, {
//         applicationSummary: {
//           applicationName,
//           archetypeCode: archetype,
//           riskTier: riskLevel,
//           domain,
//           description,
//         },
//       });

//       if (response.data.success && response.data.data?.recommendedWeights) {
//         const newWeights = response.data.data.recommendedWeights;
//         setAiWeights(newWeights);
//         setManualWeights(newWeights);
//         setSuccessMessage("✅ Weights generated successfully via LLM!");
//       } else {
//         setError("Failed to generate AI recommendations.");
//       }
//     } catch (err) {
//       console.error("Error generating AI recommendations:", err);
//       setError("Failed to generate AI recommendations. Check console for details.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleEqualSplit = () => {
//     const equal = Math.floor(100 / ATTRIBUTES.length);
//     const remainder = 100 - equal * ATTRIBUTES.length;
//     const newWeights = ATTRIBUTES.reduce((acc, attr, i) => {
//       acc[attr.label] = i === 0 ? equal + remainder : equal;
//       return acc;
//     }, {});
//     setAiWeights(newWeights);
//     setManualWeights(newWeights);
//   };

//   const handleManualChange = (attr, value) => {
//     if (!isEditable) return;
//     setManualWeights((prev) => ({ ...prev, [attr]: value }));
//   };

//   const handleEditClick = () => {
//     setIsEditable(true);
//   };

//   const handleNext = () => {
//     navigate("/risk-identification", {
//       state: {
//         applicationName,
//         domain,
//         archetype,
//         riskCode,
//         riskLevel,
//         description,
//         trustWeights: manualWeights,
//       },
//     });
//   };

//   return (
//     <Box
//       sx={{
//         flex: 1,
//         p: 4,
//         overflow: "auto",
//         bgcolor: "#F9F8F7",
//         minHeight: "100vh",
//         ml: { xs: 0, md: `${SIDEBAR_WIDTH}px` },
//         fontFamily: "'Inter', sans-serif",
//       }}
//     >
//       {/* Header */}
//       <Typography variant="h5" fontWeight={600} color="#111827" gutterBottom>
//         Trust Dimensions & Weight Assignment
//       </Typography>
//       <Typography variant="body2" color="text.secondary" mb={3}>
//         Define evaluation priorities based on risk profile and regulatory requirements.
//       </Typography>

//       {/* Inputs from Previous Stage */}
//       <Card
//         sx={{
//           mb: 3,
//           p: 3,
//           borderRadius: 3,
//           boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
//           bgcolor: "#fff",
//         }}
//       >
//         <Typography variant="subtitle1" fontWeight={600} mb={1}>
//           Inputs from Previous Stage
//         </Typography>
//         <Grid container spacing={2}>
//           <Grid item xs={6} md={3}>
//             <Typography variant="body2" color="#6b7280">
//               Application
//             </Typography>
//             <Typography variant="subtitle2" fontWeight={500}>
//               {applicationName || "N/A"}
//             </Typography>
//           </Grid>
//           <Grid item xs={6} md={3}>
//             <Typography variant="body2" color="#6b7280">
//               Archetype Code
//             </Typography>
//             <Typography variant="subtitle2" fontWeight={500}>
//               {archetype || "N/A"}
//             </Typography>
//           </Grid>
//           <Grid item xs={6} md={3}>
//             <Typography variant="body2" color="#6b7280">
//               Risk Tier
//             </Typography>
//             <Typography
//               variant="subtitle2"
//               fontWeight={600}
//               color={riskLevel?.toLowerCase() === "high" ? "#DC2626" : "#111827"}
//             >
//               {riskLevel || "N/A"}
//             </Typography>
//           </Grid>
//           <Grid item xs={6} md={3}>
//             <Typography variant="body2" color="#6b7280">
//               Domain
//             </Typography>
//             <Typography variant="subtitle2" fontWeight={500}>
//               {domain || "N/A"}
//             </Typography>
//           </Grid>
//         </Grid>

//         {/* Modifiers section */}
//         <Box mt={2}>
//           <Typography variant="body2" color="#6b7280" mb={1}>
//             Modifiers:
//           </Typography>
//           <Chip
//             label="RAG"
//             sx={{ mr: 1, bgcolor: "#E0F2FE", color: "#0284C7", fontWeight: 500 }}
//           />
//           <Chip
//             label="ENT"
//             sx={{ mr: 1, bgcolor: "#E0FBE2", color: "#15803D", fontWeight: 500 }}
//           />
//           <Chip
//             label="PII"
//             sx={{ bgcolor: "#FEE2E2", color: "#B91C1C", fontWeight: 500 }}
//           />
//         </Box>
//       </Card>

//       {/* Buttons */}
//       <Box display="flex" justifyContent="flex-start" gap={2} mb={3}>
//         <Button
//           variant="contained"
//           onClick={handleGenerateWeight}
//           startIcon={loading ? <CircularProgress size={16} /> : null}
//           disabled={loading}
//           sx={{
//             bgcolor: "#3B82F6",
//             textTransform: "none",
//             borderRadius: 2,
//             px: 3,
//             boxShadow: "0 2px 5px rgba(59,130,246,0.3)",
//             "&:hover": { bgcolor: "#2563EB" },
//           }}
//         >
//           {loading ? "Generating..." : "Generate Weight"}
//         </Button>
//         <Button
//           variant="outlined"
//           onClick={handleEqualSplit}
//           sx={{
//             borderColor: "#93C5FD",
//             color: "#2563EB",
//             textTransform: "none",
//             borderRadius: 2,
//             px: 3,
//             "&:hover": { borderColor: "#2563EB", background: "#EFF6FF" },
//           }}
//         >
//           Equal Split
//         </Button>
//       </Box>

//       {/* Weight Cards */}
//       <Grid container spacing={3}>
//         {/* LLM Generated */}
//         <Grid item xs={12} md={6}>
//           <Card
//             sx={{
//               p: 3,
//               borderRadius: 3,
//               boxShadow: "0 3px 6px rgba(0,0,0,0.05)",
//               bgcolor: "#fff",
//             }}
//           >
//             <Typography variant="subtitle1" fontWeight={600} mb={2}>
//               Weight Generated via LLM
//             </Typography>

//             <LinearProgress
//               variant="determinate"
//               value={totalAI}
//               sx={{
//                 height: 10,
//                 borderRadius: 5,
//                 mb: 1,
//                 "& .MuiLinearProgress-bar": { bgcolor: "#22C55E" },
//               }}
//             />
//             <Typography variant="body2" mb={2}>
//               Total Weight: <strong>{totalAI}%</strong>
//             </Typography>

//             {ATTRIBUTES.map((attr) => (
//               <Box key={attr.label} mb={2}>
//                 <Typography variant="body2" mb={0.5}>
//                   {attr.label}
//                 </Typography>
//                 <Slider
//                   value={aiWeights[attr.label]}
//                   disabled
//                   min={0}
//                   max={100}
//                   step={1}
//                   valueLabelDisplay="ra
//                   auto"
//                   sx={{ color: attr.color }}
//                 />
//               </Box>
//             ))}
//           </Card>
//         </Grid>

//         {/* Manually Set */}
//         <Grid item xs={12} md={6}>
//           <Card
//             sx={{
//               p: 3,
//               borderRadius: 3,
//               boxShadow: "0 3px 6px rgba(0,0,0,0.05)",
//               bgcolor: "#fff",
//             }}
//           >
//             <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
//               <Typography variant="subtitle1" fontWeight={600}>
//                 Manually Set Weight
//               </Typography>
//               <Button
//                 variant="text"
//                 onClick={handleEditClick}
//                 disabled={isEditable}
//                 sx={{ color: "#2563EB", textTransform: "none", fontWeight: 600 }}
//               >
//                 Edit
//               </Button>
//             </Box>

//             <LinearProgress
//               variant="determinate"
//               value={Math.min(totalManual, 100)}
//               sx={{
//                 height: 10,
//                 borderRadius: 5,
//                 mb: 1,
//                 "& .MuiLinearProgress-bar": {
//                   bgcolor: totalManual > 100 ? "#F87171" : "#22C55E",
//                 },
//               }}
//             />
//             <Typography
//               variant="body2"
//               mb={1}
//               color={totalManual > 100 ? "error" : "#111827"}
//             >
//               Total Weight: <strong>{totalManual}%</strong>
//             </Typography>
//             {totalManual > 100 && (
//               <Typography variant="caption" color="error" mb={2} display="block">
//                 ⚠️ Total weight should not exceed 100%
//               </Typography>
//             )}

//             {ATTRIBUTES.map((attr) => (
//               <Box key={attr.label} mb={2}>
//                 <Typography variant="body2" mb={0.5}>
//                   {attr.label}
//                 </Typography>
//                 <Slider
//                   value={manualWeights[attr.label]}
//                   onChange={(_, val) => handleManualChange(attr.label, val)}
//                   disabled={!isEditable}
//                   min={0}
//                   max={100}
//                   step={1}
//                   valueLabelDisplay="auto"
//                   sx={{
//                     color:
//                       totalManual > 100
//                         ? "#F87171"
//                         : isEditable
//                         ? attr.color
//                         : "#9CA3AF",
//                   }}
//                 />
//               </Box>
//             ))}
//           </Card>
//         </Grid>
//       </Grid>

//       {/* Next Button */}
//       <Box display="flex" justifyContent="flex-end" mt={4}>
//         <Button
//           variant="contained"
//           color="primary"
//           onClick={handleNext}
//           disabled={totalManual > 100 || totalManual < 100}
//           sx={{
//             bgcolor: "#3B82F6",
//             textTransform: "none",
//             borderRadius: 3,
//             px: 4,
//             py: 1,
//             fontWeight: 600,
//             boxShadow: "0 3px 8px rgba(59,130,246,0.35)",
//             "&:hover": { bgcolor: "#2563EB" },
//           }}
//         >
//           Next →
//         </Button>
//       </Box>
//     </Box>
//   );
// };

// export default TrustIndexSettingsPage;

// import React, { useState } from "react";
// import {
//   Box,
//   Typography,
//   Grid,
//   Slider,
//   Button,
//   Card,
//   CardContent,
//   Divider,
//   Alert,
//   LinearProgress,
//   CircularProgress,
// } from "@mui/material";
// import { useNavigate, useLocation } from "react-router-dom";
// import axios from "axios";
// import { InfoOutlined } from "@mui/icons-material";

// const ATTRIBUTES = [
//   "Accuracy",
//   "Fairness",
//   "Privacy",
//   "Ethics",
//   "Robustness",
//   "Security",
//   "Reliability",
// ];

// const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
// const SIDEBAR_WIDTH = 64;

// const TrustIndexSettingsPage = () => {
//   const navigate = useNavigate();
//   const location = useLocation();

//   const {
//     applicationName,
//     domain,
//     archetype,
//     riskCode,
//     riskLevel,
//     description,
//   } = location.state || {};

//   const [weights, setWeights] = useState(
//     ATTRIBUTES.reduce((acc, attr) => ({ ...acc, [attr]: 0 }), {})
//   );
//   const [loading, setLoading] = useState(false);
//   const [successMessage, setSuccessMessage] = useState(null);
//   const [error, setError] = useState(null);

//   const total = Object.values(weights).reduce((sum, v) => sum + v, 0);

//   // Handle slider change
//   const handleSliderChange = (attr, value) => {
//     setWeights((prev) => ({ ...prev, [attr]: value }));
//   };

//   // Equal Split
//   const handleEqualSplit = () => {
//     const equalValue = Math.floor(100 / ATTRIBUTES.length);
//     const remainder = 100 - equalValue * ATTRIBUTES.length;
//     const newWeights = ATTRIBUTES.reduce((acc, attr, index) => {
//       acc[attr] = index === 0 ? equalValue + remainder : equalValue;
//       return acc;
//     }, {});
//     setWeights(newWeights);
//   };

//   // Reset all weights to 0
//   const handleReset = () => {
//     const resetWeights = ATTRIBUTES.reduce((acc, attr) => ({ ...acc, [attr]: 0 }), {});
//     setWeights(resetWeights);
//     setError(null);
//     setSuccessMessage(null);
//   };

//   // Generate AI Recommendations
//   const handleGenerateRecommendations = async () => {
//     setLoading(true);
//     setError(null);
//     setSuccessMessage(null);

//     try {
//       const response = await axios.post(`${baseUrl}/trust-index-settings`, {
//         applicationSummary: {
//           applicationName,
//           archetypeCode: archetype,
//           riskTier: riskLevel,
//           domain,
//           description,
//         },
//         currentWeights: weights,
//       });

//       if (response.data.success && response.data.data?.recommendedWeights) {
//         const newWeights = response.data.data.recommendedWeights;
//         setWeights(newWeights);
//         setSuccessMessage("✅ AI recommendations applied successfully!");
//       } else {
//         setError("Failed to generate AI recommendations.");
//       }
//     } catch (err) {
//       console.error("Error generating AI recommendations:", err);
//       setError("Failed to generate AI recommendations. Check console for details.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <Box
//       sx={{
//         flex: 1,
//         p: 4,
//         overflow: "auto",
//         bgcolor: "#fafafa",
//         minHeight: "100vh",
//         ml: { xs: 0, md: `${SIDEBAR_WIDTH}px` },
//       }}
//     >
//       {/* Header */}
//       <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
//         <Typography variant="h5" fontWeight="bold">
//           Trust Index Settings
//         </Typography>
//       </Box>

//       {/* Description */}
//       <Typography variant="body2" color="text.secondary" mb={3}>
//         Adjust weights to reflect your application's trust priorities.
//       </Typography>

//       {/* Inputs from Previous Stage */}
//       <Card sx={{ mb: 3, p: 2 }}>
//         <Typography variant="h6" gutterBottom>
//           Inputs from Previous Stage
//         </Typography>
//         <Grid container spacing={2}>
//           <Grid item xs={6} md={3}>
//             <Typography variant="body2" color="textSecondary">
//               Application
//             </Typography>
//             <Typography variant="subtitle2">{applicationName || "N/A"}</Typography>
//           </Grid>
//           <Grid item xs={6} md={3}>
//             <Typography variant="body2" color="textSecondary">
//               Archetype Code
//             </Typography>
//             <Typography variant="subtitle2">{archetype || "N/A"}</Typography>
//           </Grid>
//           <Grid item xs={6} md={3}>
//             <Typography variant="body2" color="textSecondary">
//               Risk Tier
//             </Typography>
//             <Typography
//               variant="subtitle2"
//               color={riskLevel?.toLowerCase() === "high" ? "error.main" : "inherit"}
//             >
//               {riskLevel || "N/A"}
//             </Typography>
//           </Grid>
//           <Grid item xs={6} md={3}>
//             <Typography variant="body2" color="textSecondary">
//               Domain
//             </Typography>
//             <Typography variant="subtitle2">{domain || "N/A"}</Typography>
//           </Grid>
//         </Grid>
//       </Card>

//       {/* Action Buttons */}
//       <Box display="flex" justifyContent="flex-end" gap={2} mb={2}>
//         <Button
//           variant="contained"
//           color="primary"
//           onClick={handleGenerateRecommendations}
//           startIcon={loading ? <CircularProgress size={16} /> : null}
//           disabled={loading}
//         >
//           {loading ? "Generating..." : "Get AI Recommendations"}
//         </Button>
//         <Button variant="outlined" onClick={handleEqualSplit}>
//           Equal Split
//         </Button>
//         <Button variant="outlined" color="secondary" onClick={handleReset}>
//           Reset
//         </Button>
//       </Box>

//       {/* Total Weight Bar */}
//       <Card sx={{ mb: 3, p: 3 }}>
//         <Typography variant="subtitle1" mb={1}>
//           Total Weight
//         </Typography>
//         <LinearProgress
//           variant="determinate"
//           value={Math.min(total, 200)} // clamp at 200
//           sx={{
//             height: 10,
//             borderRadius: 5,
//             bgcolor: "#e0e0e0",
//             "& .MuiLinearProgress-bar": {
//               bgcolor: total <= 100 ? "#4caf50" : "#f44336", // green or red
//             },
//           }}
//         />
//         <Box display="flex" justifyContent="space-between" mt={1}>
//           <Typography
//             variant="body2"
//             color={total <= 100 ? "text.primary" : "error"}
//             fontWeight="bold"
//           >
//             {total}%
//           </Typography>
//           <Typography
//             variant="body2"
//             color={total <= 100 ? "text.secondary" : "error"}
//           >
//             {total > 100 ? "⚠️ Must equal 100%" : "Target: 100%"}
//           </Typography>
//         </Box>
//       </Card>

//       {/* Weight Distribution */}
//       <Card sx={{ p: 3 }}>
//         <Typography variant="h6" mb={2}>
//           Weight Distribution
//         </Typography>
//         <Grid container spacing={1}> {/* reduced spacing */}
//           {ATTRIBUTES.map((attr) => (
//             <Grid item xs={12} key={attr}>
//               <Box
//                 sx={{
//                   display: "flex",
//                   alignItems: "center",
//                   gap: 2,
//                   py: 0.5, // ✅ compact vertical padding
//                   borderBottom: "1px solid #eee", // subtle divider line between attributes
//                 }}
//               >
//                 {/* Attribute Name */}
//                 <Typography sx={{ width: 130, fontSize: 14, fontWeight: 500 }}>
//                   {attr}
//                 </Typography>

//                 {/* Slider */}
//                 <Box sx={{ flex: 1 }}>
//                   <Slider
//                     value={weights[attr]}
//                     onChange={(_, val) => handleSliderChange(attr, val)}
//                     step={1}
//                     min={0}
//                     max={100}
//                     valueLabelDisplay="auto"
//                     sx={{
//                       height: 6,
//                       "& .MuiSlider-thumb": { width: 16, height: 16 },
//                       "& .MuiSlider-track": { border: "none" },
//                       "& .MuiSlider-rail": { opacity: 0.3 },
//                     }}
//                   />
//                 </Box>

//                 {/* Value */}
//                 <Typography sx={{ width: 30, fontSize: 14, textAlign: "right" }}>
//                   {weights[attr]}
//                 </Typography>
//               </Box>
//             </Grid>
//           ))}
//         </Grid>

//         {/* <Grid container spacing={3}>
//           {ATTRIBUTES.map((attr) => (
//             <Grid item xs={12} key={attr}>
//               <Card
//                 variant="outlined"
//                 sx={{
//                   p: 2,
//                   borderRadius: 2,
//                   backgroundColor: "#fdfdfd",
//                   boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
//                 }}
//               >
//                 <Typography variant="subtitle1" fontWeight="bold">
//                   {attr}
//                 </Typography>
//                 <Typography variant="body2" color="text.secondary" mb={1}>
//                   Baseline: {Math.floor(100 / ATTRIBUTES.length)}%
//                 </Typography>
//                 <Slider
//                   value={weights[attr]}
//                   onChange={(_, val) => handleSliderChange(attr, val)}
//                   step={1}
//                   min={0}
//                   max={100}
//                   valueLabelDisplay="auto"
//                 />
//                 <Typography
//                   variant="body2"
//                   color="text.secondary"
//                   sx={{ mt: 1, display: "flex", justifyContent: "space-between" }}
//                 >
//                   <span>
//                     {weights[attr]}%{" "}
//                     {weights[attr] >
//                     Math.floor(100 / ATTRIBUTES.length)
//                       ? `(+${weights[attr] -
//                           Math.floor(100 / ATTRIBUTES.length)}% from baseline)`
//                       : ""}
//                   </span>
//                   <span style={{ color: "#888" }}>NIST AI RMF – Measure</span>
//                 </Typography>
//               </Card>
//             </Grid>
//           ))}
//         </Grid> */}
//       </Card>

//       {/* Alerts */}
//       {error && (
//         <Alert severity="error" sx={{ mt: 3 }}>
//           {error}
//         </Alert>
//       )}
//       {successMessage && (
//         <Alert severity="success" sx={{ mt: 3 }}>
//           {successMessage}
//         </Alert>
//       )}

//       {/* Navigation Buttons */}
//       <Box display="flex" justifyContent="flex-end" mt={4} gap={2}>
//         <Button variant="outlined" onClick={() => navigate(-1)}>
//           Back
//         </Button>
//         <Button
//           variant="contained"
//           color="primary"
//           disabled={total > 100}
//           onClick={() =>
//             navigate("/risk-identification", {
//               state: {
//                 applicationName,
//                 domain,
//                 archetype,
//                 riskCode,
//                 riskLevel,
//                 description,
//                 trustWeights: weights,
//               },
//             })
//           }
//         >
//           Next → Risk Identification
//         </Button>
//       </Box>
//     </Box>
//   );
// };

// export default TrustIndexSettingsPage;
